import React, { type CSSProperties } from 'react';

import type { UniqueIdentifier } from '@dnd-kit/core';
import { type UseSortableArguments, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Button from 'common/uiV3/Button';
import Flex from 'common/uiV3/Flex';
import { GripVertical } from 'lucide-react';

export type ListItemComponentType<
  T,
  TElement extends HTMLElement,
> = React.ForwardRefExoticComponent<
  ListItemComponentProps<T, TElement> & React.RefAttributes<TElement>
>;

export type ListItemComponentProps<T, TElement extends HTMLElement> = {
  item: T;
  id: UniqueIdentifier;
  isDragging?: boolean;
  isOver?: boolean;
  handleProps?: any;
  style?: CSSProperties;
} & Omit<React.HTMLAttributes<TElement>, 'id'>;

type SortableListItemProps<T, TElement extends HTMLElement> = {
  id: UniqueIdentifier;
  item: T;
  ItemComponent: ListItemComponentType<T, TElement>;
  disabled?: boolean;
  sortableProps?: Omit<UseSortableArguments, 'id'>;
};

const SortableListItemNotMemoized = function SortableListItem<T, TElement extends HTMLElement>({
  id,
  item,
  ItemComponent,
  disabled,
  sortableProps,
}: SortableListItemProps<T, TElement>) {
  const { attributes, isDragging, listeners, setNodeRef, transform, transition, isOver } =
    useSortable({
      id,
      disabled,
      ...sortableProps,
    });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? undefined : transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <ItemComponent
      id={id}
      item={item}
      isDragging={isDragging}
      isOver={isOver}
      handleProps={{
        ...attributes,
        ...listeners,
      }}
      ref={setNodeRef as React.Ref<TElement>}
      style={style}
    />
  );
};

export const SortableListItemHandle = ({ handleProps }: { handleProps: any }) => {
  return (
    <Flex {...handleProps} tabIndex={-1}>
      <Button
        variant="unstyled"
        size="small"
        iconOnly
        className="w-3.5 hover:cursor-move text-muted-foreground">
        <GripVertical />
      </Button>
    </Flex>
  );
};

export const SortableListItem = React.memo(
  SortableListItemNotMemoized,
) as typeof SortableListItemNotMemoized;
